import {
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FORM_CONTROL_FOCUS_WITHIN_CLASS } from '@/components/ui/form-control-styles'
import { Skeleton } from '@/components/ui/skeleton'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { SettingsCard } from '@/components/settings/SettingsCard'
import { ListPagination } from '@/components/shared/ListPagination'
import { FileUploadDropzone } from '@/components/shared/FileUploadDropzone'
import {
  AttachmentPreviewGrid,
  ConfirmDeleteDialog,
  type AttachmentRef,
} from '@/components/shared'
import { useListParams } from '@/hooks/useListParams'
import {
  useReusableDocuments,
  useCreateReusableDocument,
  useDeleteReusableDocument,
  useRenameReusableDocument,
} from '@/hooks/useSettings'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage, getApiFieldError } from '@/services/api'
import { formatDate } from '@/lib/format'
import { getFileSizeLimitMb, isFileTooLarge } from '@/lib/uploadValidation'
import {
  documentBaseName,
  documentExtension,
  validateDocumentRename,
} from '@/lib/documentRename'
import { cn } from '@/lib/utils'
import { isDocumentFileExtension } from '@/lib/constants'
import type { ReusableDocument, ReusableDocumentType } from '@/types'

const NAME_MAX = 150
const ACCEPT = '.pdf,.docx,.xlsx,.png,.jpg,.jpeg'
const COLUMN_COUNT = 5

const sectionCardClass =
  'rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_rgba(38,45,66,0.05),0_8px_16px_-12px_rgba(38,45,66,0.1)]'

const thClass =
  'h-10 border-b border-border bg-muted px-3 py-2 text-xs font-medium normal-case tracking-normal text-muted-foreground'

const tdClass = 'h-16 border-b border-border px-3 py-2 align-middle text-sm'

const uploadBtnClass =
  'h-10 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 text-base font-medium text-white shadow-none hover:bg-[#e6511c]'

const actionButtonClass =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#f4f5f8] p-1.5 text-[#272c36] transition-colors hover:bg-[#ebeef4] disabled:cursor-not-allowed disabled:opacity-50'
function defaultDocumentType(_file: File): ReusableDocumentType {
  return 'other'
}

function normalizeDocumentName(name: string) {
  try {
    return decodeURIComponent(name).trim().slice(0, NAME_MAX)
  } catch {
    return name.trim().slice(0, NAME_MAX)
  }
}

function defaultDocumentName(file: File) {
  return normalizeDocumentName(file.name)
}

// 2.11.19 — Upload & manage reusable documents. The embedded documents-tab
// variant follows the compact card layout from the provided mockup and uploads
// immediately from the + Upload button without a form popup.
export function DocumentLibraryPanel({
  embedded = false,
}: {
  embedded?: boolean
}) {
  const { t } = useTranslation('settings')
  const { page, setPage, limit, setLimit, query } = useListParams()
  const { data, isLoading, isError } = useReusableDocuments(query)
  const create = useCreateReusableDocument()
  const rename = useRenameReusableDocument()
  const remove = useDeleteReusableDocument()

  const [toDelete, setToDelete] = useState<ReusableDocument | null>(null)
  const [isDraggingUpload, setIsDraggingUpload] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')
  const [renameError, setRenameError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const renameSubmissionInFlight = useRef(false)

  const rows = useMemo(() => data?.data ?? [], [data])
  const attachmentItems = useMemo<AttachmentRef[]>(
    () =>
      rows.map(doc => ({
        id: doc.id,
        file_id: doc.file_id,
        name: `${documentBaseName(doc)}${documentExtension(doc)}`,
        url: doc.file_url,
        mime_type: doc.file_mime_type,
        size: doc.file_size,
        created_at: doc.created_at,
        source: 'upload',
      })),
    [rows]
  )

  function triggerUpload() {
    if (create.isPending) return
    fileInput.current?.click()
  }

  async function uploadFiles(files: FileList | File[]) {
    if (files.length === 0 || create.isPending) return

    let uploaded = 0
    for (const file of Array.from(files)) {
      const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
      if (!isDocumentFileExtension(extension)) {
        toast({
          variant: 'destructive',
          title: t('documentLibrary.toast.unsupportedFormat'),
        })
        continue
      }
      if (isFileTooLarge(file)) {
        toast({
          variant: 'destructive',
          title: t('shared:attachFiles.tooLarge', {
            max: getFileSizeLimitMb(),
          }),
        })
        continue
      }

      try {
        await create.mutateAsync({
          name: defaultDocumentName(file),
          file,
          document_type: defaultDocumentType(file),
          is_active: true,
        })
        uploaded += 1
      } catch (error) {
        toast({ variant: 'destructive', title: getApiErrorMessage(error) })
      }
    }

    if (uploaded > 0) {
      toast({
        variant: 'success',
        title: t('documentLibrary.toast.uploaded'),
      })
    }
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (files.length > 0) void uploadFiles(files)
  }

  function handleUploadDrag(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault()
    event.stopPropagation()
    if (create.isPending) return
    setIsDraggingUpload(event.type === 'dragenter' || event.type === 'dragover')
  }

  function handleUploadDrop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault()
    event.stopPropagation()
    setIsDraggingUpload(false)
    if (create.isPending) return

    const files = event.dataTransfer.files
    if (files.length > 0) void uploadFiles(files)
  }

  function confirmDelete() {
    if (!toDelete) return

    remove.mutate(toDelete.id, {
      onSuccess: () => {
        toast({ variant: 'success', title: t('documentLibrary.toast.deleted') })
        setToDelete(null)
      },
      onError: error =>
        toast({ variant: 'destructive', title: getApiErrorMessage(error) }),
    })
  }

  async function renameEmbeddedDocument(item: AttachmentRef, name: string) {
    if (!item.id) return
    await rename.mutateAsync({ id: item.id, body: { name } })
    toast({
      variant: 'success',
      title: t('documentLibrary.toast.renamed'),
    })
  }

  function beginRename(doc: ReusableDocument) {
    if (rename.isPending || (editingId !== null && editingId !== doc.id)) return
    renameSubmissionInFlight.current = false
    setEditingId(doc.id)
    setRenameDraft(documentBaseName(doc))
    setRenameError(null)
  }

  function cancelRename() {
    renameSubmissionInFlight.current = false
    setEditingId(null)
    setRenameDraft('')
    setRenameError(null)
  }

  function validateRename(doc: ReusableDocument, value = renameDraft) {
    const result = validateDocumentRename(
      value,
      documentExtension(doc),
      NAME_MAX
    )
    if (result === 'required')
      return t('documentLibrary.validation.renameRequired')
    if (result === 'invalid')
      return t('documentLibrary.validation.renameInvalid')
    if (result === 'tooLong')
      return t('documentLibrary.validation.nameMax', { max: NAME_MAX })
    return null
  }

  function saveRename(doc: ReusableDocument) {
    if (rename.isPending || editingId !== doc.id) return

    const error = validateRename(doc)
    setRenameError(error)
    if (error) return

    const name = renameDraft.trim()
    if (name === documentBaseName(doc)) {
      cancelRename()
      return
    }

    renameSubmissionInFlight.current = true
    rename.mutate(
      { id: doc.id, body: { name } },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: t('documentLibrary.toast.renamed'),
          })
          cancelRename()
        },
        onError: error => {
          renameSubmissionInFlight.current = false
          const fieldError = getApiFieldError(error, 'name')
          if (fieldError) {
            setRenameError(fieldError)
            return
          }
          toast({
            variant: 'destructive',
            title: getApiErrorMessage(error),
          })
        },
      }
    )
  }

  function handleRenameBlur() {
    if (renameSubmissionInFlight.current) return
    cancelRename()
  }

  function handleRenameKeyDown(
    event: React.KeyboardEvent<HTMLInputElement>,
    doc: ReusableDocument
  ) {
    if (event.key === 'Escape') {
      event.preventDefault()
      cancelRename()
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      saveRename(doc)
    }
  }

  function renameField(doc: ReusableDocument) {
    const hasError = !!renameError

    return (
      <div className='flex w-full min-w-0 flex-col gap-1'>
        <div
          className={cn(
            'flex h-7 w-full min-w-0 items-center overflow-hidden rounded-md border bg-white px-2 text-sm font-medium leading-[1.4]',
            FORM_CONTROL_FOCUS_WITHIN_CLASS,
            hasError
              ? 'border-[1.5px] border-[#d63b33] shadow-[0_0_0_2px_#fff1ea]'
              : 'border-[#e7e9ef]'
          )}
        >
          <input
            autoFocus
            value={renameDraft}
            onChange={event => {
              const value = event.target.value
              setRenameDraft(value)
              setRenameError(validateRename(doc, value))
            }}
            onFocus={event => event.currentTarget.select()}
            onBlur={handleRenameBlur}
            onKeyDown={event => handleRenameKeyDown(event, doc)}
            disabled={rename.isPending}
            aria-invalid={hasError}
            aria-describedby={hasError ? `rename-error-${doc.id}` : undefined}
            aria-label={t('documentLibrary.fields.name')}
            className='min-w-0 flex-1 border-0 bg-transparent p-0 text-sm font-medium leading-[1.4] text-[#171a20] outline-none disabled:cursor-wait'
          />
          <span className='shrink-0 text-sm font-medium leading-[1.4] text-[#171a20]'>
            {documentExtension(doc)}
          </span>
        </div>
        {hasError ? (
          <p
            id={`rename-error-${doc.id}`}
            className='text-xs leading-4 text-[#991b1b]'
          >
            {renameError}
          </p>
        ) : null}
      </div>
    )
  }

  const uploadButton = (
    <Button
      className={uploadBtnClass}
      onClick={triggerUpload}
      disabled={create.isPending}
    >
      <Plus className='mr-1.5 h-4 w-4' />
      {create.isPending
        ? t('attachFiles.uploading', { ns: 'shared' })
        : t('documentLibrary.uploadDocument')}
    </Button>
  )

  const embeddedBody = (
    <section className={sectionCardClass}>
      <input
        ref={fileInput}
        type='file'
        multiple
        accept={ACCEPT}
        className='hidden'
        disabled={create.isPending}
        onChange={handleFileChange}
      />

      <div className='flex flex-col gap-5 py-4'>
        <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
          <div className='min-w-0 flex-1'>
            <div className='flex min-w-0 items-center border-l-4 border-[#ff5a1f] px-6 py-1'>
              <h2 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
                {t('documentLibrary.title')}
              </h2>
            </div>
          </div>
        </div>

        <div className='px-4 sm:px-5 lg:px-6'>
          <div className='mb-2 text-xs font-medium leading-4 text-[#171a20]'>
            {t('documentLibrary.uploadLabel')}{' '}
            <span className='text-[#d63b33]'>*</span>
          </div>

          <FileUploadDropzone
            className='mb-5 rounded-xl [&_span]:leading-4'
            onClick={triggerUpload}
            onDragEnter={handleUploadDrag}
            onDragOver={handleUploadDrag}
            onDragLeave={handleUploadDrag}
            onDrop={handleUploadDrop}
            disabled={create.isPending}
            loading={create.isPending}
            loadingText={t('attachFiles.uploading', { ns: 'shared' })}
            dragActive={isDraggingUpload}
            primaryText={t('documentLibrary.dropzoneTitle')}
            secondaryText={t('documentLibrary.dropzoneHint', {
              max: getFileSizeLimitMb(),
            })}
          />

          {isLoading ? (
            <div className='flex flex-wrap items-start gap-4 pt-4'>
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className='w-[160px] shrink-0 overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_rgba(38,45,66,0.1),0_1px_4px_rgba(38,45,66,0.05)]'
                >
                  <Skeleton className='h-[120px] w-full rounded-none' />
                  <div className='space-y-2 px-4 py-3'>
                    <Skeleton className='h-5 w-24' />
                    <Skeleton className='h-4 w-20' />
                  </div>
                </div>
              ))}
            </div>
          ) : isError ? (
            <div className='rounded-2xl border border-dashed border-[#e7e9ef] bg-[#fbfbfc] px-6 py-10 text-center text-destructive'>
              {t('documentLibrary.loadError')}
            </div>
          ) : rows.length === 0 ? (
            <div className='rounded-2xl border border-dashed border-[#e7e9ef] bg-[#fbfbfc] px-6 py-10 text-center text-[#6c7285]'>
              {t('documentLibrary.empty')}
            </div>
          ) : (
            <AttachmentPreviewGrid
              items={attachmentItems}
              removeLabel={t('common:actions.delete')}
              onRename={renameEmbeddedDocument}
              onRemove={item => {
                const target = rows.find(doc => doc.id === item.id)
                if (target) setToDelete(target)
              }}
              disabled={rename.isPending}
            />
          )}
        </div>
      </div>
    </section>
  )

  const tableBody = (
    <>
      <input
        ref={fileInput}
        type='file'
        multiple
        accept={ACCEPT}
        className='hidden'
        disabled={create.isPending}
        onChange={handleFileChange}
      />

      <div className='px-6 py-4'>
        <FileUploadDropzone
          onClick={triggerUpload}
          onDragEnter={handleUploadDrag}
          onDragOver={handleUploadDrag}
          onDragLeave={handleUploadDrag}
          onDrop={handleUploadDrop}
          disabled={create.isPending}
          loading={create.isPending}
          loadingText={t('attachFiles.uploading', { ns: 'shared' })}
          dragActive={isDraggingUpload}
          primaryText={t('documentLibrary.dropzoneTitle')}
          secondaryText={t('documentLibrary.dropzoneHint', {
            max: getFileSizeLimitMb(),
          })}
        />
      </div>

      <div className='overflow-x-auto'>
        <table className='w-full min-w-[720px] caption-bottom text-sm'>
          <TableHeader>
            <TableRow className='border-0'>
              <TableHead className={thClass}>{t('common.name')}</TableHead>
              <TableHead className={cn(thClass, 'w-[180px]')}>
                {t('documentLibrary.columns.documentType')}
              </TableHead>
              <TableHead className={cn(thClass, 'w-[120px]')}>
                {t('common.status')}
              </TableHead>
              <TableHead className={cn(thClass, 'w-[140px]')}>
                {t('documentLibrary.columns.uploaded')}
              </TableHead>
              <TableHead className={cn(thClass, 'w-[120px] pr-4 text-right')}>
                <span className='sr-only'>{t('common.actions')}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 4 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={COLUMN_COUNT} className={tdClass}>
                    <Skeleton className='h-6 w-full' />
                  </TableCell>
                </TableRow>
              ))}

            {isError && !isLoading && (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  className={cn(tdClass, 'py-10 text-center text-destructive')}
                >
                  {t('documentLibrary.loadError')}
                </TableCell>
              </TableRow>
            )}

            {!isLoading && !isError && rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  className={cn(
                    tdClass,
                    'py-10 text-center text-muted-foreground'
                  )}
                >
                  {t('documentLibrary.empty')}
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              !isError &&
              rows.map(doc => {
                return (
                  <TableRow key={doc.id} className='border-0'>
                    <TableCell className={cn(tdClass)}>
                      {editingId === doc.id ? renameField(doc) : doc.name}
                    </TableCell>
                    <TableCell className={cn(tdClass)}>
                      {doc.document_type
                        ? t(`documentLibrary.docTypes.${doc.document_type}`)
                        : '—'}
                    </TableCell>
                    <TableCell className={cn(tdClass)}>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium leading-4',
                          doc.is_active
                            ? 'bg-[#D1FAE5] text-[#065F46]'
                            : 'bg-[#f4f5f8] text-[#171a20]'
                        )}
                      >
                        {doc.is_active
                          ? t('common.active')
                          : t('common.inactive')}
                      </span>
                    </TableCell>
                    <TableCell className={cn(tdClass)}>
                      {formatDate(doc.created_at)}
                    </TableCell>
                    <TableCell className={cn(tdClass, 'pr-4')}>
                      <div className='flex items-center justify-end gap-2'>
                        {doc.file_url ? (
                          <a
                            href={doc.file_url}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={actionButtonClass}
                            title={t('common:actions.view')}
                          >
                            <ExternalLink className='h-5 w-5' />
                          </a>
                        ) : null}
                        {editingId !== doc.id ? (
                          <button
                            type='button'
                            className={actionButtonClass}
                            title={t('common:actions.edit')}
                            onClick={() => beginRename(doc)}
                            disabled={rename.isPending}
                          >
                            <Pencil className='h-5 w-5' />
                          </button>
                        ) : null}
                        <button
                          type='button'
                          className={actionButtonClass}
                          title={t('common:actions.delete')}
                          onClick={() => {
                            if (editingId === doc.id) cancelRename()
                            setToDelete(doc)
                          }}
                        >
                          <Trash2 className='h-5 w-5' />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
          </TableBody>
        </table>
      </div>

      <ListPagination
        metadata={data?.metadata}
        page={page}
        pageSize={limit}
        onPageChange={setPage}
        onPageSizeChange={setLimit}
      />
    </>
  )

  return (
    <>
      {embedded ? (
        embeddedBody
      ) : (
        <SettingsCard
          title={t('documentLibrary.title')}
          contentClassName='flex flex-col gap-0 p-0'
          actions={uploadButton}
        >
          {tableBody}
        </SettingsCard>
      )}

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={next => !remove.isPending && !next && setToDelete(null)}
        title={t('documentLibrary.dialog.deleteTitle')}
        description={t('documentLibrary.dialog.deleteMessage')}
        confirmLabel={t('documentLibrary.dialog.deleteConfirm')}
        onConfirm={confirmDelete}
        busy={remove.isPending}
      />
    </>
  )
}

export default DocumentLibraryPanel
